/**
 * Portal **chat assistant** — the typed face of an app's `chat` interface
 * (ADR-0028 Block E).
 *
 * The text twin of the Jarvis voice widget: a launcher button that opens a
 * docked chat panel. It talks to the SAME server brain (`/chat/turn`, SSE,
 * Claude tool-use under the caller's RLS + AssistantScope) — only the surface
 * differs (you type instead of speak, and the answer may use light markdown).
 * No audio, so this is a much smaller widget than `VoiceAssistant`; it reuses
 * the shared SSE pipe + event contract from `lib/portalVoice`.
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Columns3,
  Database,
  Download,
  File,
  FileSpreadsheet,
  FileText,
  History,
  Image as ImageIcon,
  Maximize2,
  MessageSquare,
  Pencil,
  Plus,
  Presentation,
  RefreshCw,
  Search,
  Send,
  Sparkles,
  Trash2,
  X,
  type LucideIcon,
} from "lucide-react";

import {
  AttachButton,
  AttachmentChips,
  ModelVisionBadge,
  imagePreviewUrl,
  type AttachedFile,
  type ModelVision,
} from "@/components/assistant/ChatAttachments";
import { ResultChart } from "@/components/charts/ResultChart";
import { ChatMarkdown } from "@/components/ui/ChatMarkdown";
import {
  portalApi,
  type AssistantSessionSummary,
  type MessageBranch,
  type PortalAssistantStarter,
} from "@/lib/api-portal";
import {
  streamChatTurn,
  type VoiceEvent,
  type VoicePreview,
  type VoiceToolRows,
} from "@/lib/portalVoice";

/** Where a forked turn hangs (ADR-0072) + how much of the visible path survives it.
 *  `parentId` is the wire value (a node id, or `"root"` for a new opening question);
 *  `keepUpTo` is the slice index of the messages that stay on screen. */
interface ForkTarget {
  parentId: string;
  keepUpTo: number;
}

/** The reserved "hang this off the ROOT" parent — mirrors the backend
 *  `portal.sessions.ROOT_PARENT`. Editing the FIRST question needs it: an omitted
 *  parent means "continue the active branch", which would append instead of fork. */
const ROOT_PARENT = "root";

interface ToolChipItem {
  id: string;
  name: string;
  preview?: VoicePreview;
  /** The actual rows the tool returned (chat surface only) — rendered as a
   *  compact "what I read" table so a fabrication is immediately visible. */
  result?: VoiceToolRows;
}

interface ChatMessage {
  /** The persisted node's id (ADR-0072) — absent on an optimistic, not-yet-saved turn.
   *  Fork / edit / regenerate all POST a turn with `parent_id` set to one of these. */
  id?: string;
  parentId?: string | null;
  /** Where this message sits among its siblings — renders the `‹ 2/3 ›` switcher. */
  branch?: MessageBranch | null;
  role: "user" | "assistant";
  text: string;
  /** Live "working…" note while a tool runs (cleared when the answer streams). */
  tool?: string;
  /** The tool calls this assistant turn made (read/write), shown as compact chips
   *  — the chat twin of the voice HUD's tool cards (ADR-0028 B-E). */
  tools?: ToolChipItem[];
  error?: boolean;
  /** Files the user attached to THIS message (ADR-0034) — rendered with the bubble. */
  attachments?: AttachedFile[];
}

const OP_ICON: Record<string, typeof Database> = {
  query: Search,
  describe: Columns3,
  create: Plus,
  update: Pencil,
  delete: Trash2,
};

/** A compact, render-ready summary of ONE tool call — the chat counterpart of
 *  `VoiceToolCards` (which is the animated voice-orb version). */
function ToolChip({ name, p }: { name: string; p: VoicePreview }) {
  const { t } = useTranslation();
  const Icon = OP_ICON[p.op ?? ""] ?? Database;
  const isQuery = p.op === "query" || name === "query_table" || name === "query_projection";
  const isDescribe = p.op === "describe" || name === "describe_dataset";
  const title = p.table || (name === "list_tables" ? t("chat.toolTables", "tables") : name);
  const detail = p.error
    ? t("chat.toolError", "error")
    : isQuery
      ? `${p.total ?? p.count ?? 0} ${t("chat.toolResults", "res.")}`
      : isDescribe && p.fields != null
        ? `${p.fields} ${t("chat.toolFields", "fields")}`
        : p.done
          ? t("chat.toolDone", "done")
          : "";
  const tone = p.error
    ? "border-danger/40 text-danger"
    : p.done
      ? "border-success/40 text-success"
      : "border-border/60 text-muted-foreground";
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-md border bg-surface px-1.5 py-0.5 text-[11px] ${tone}`}
    >
      <Icon className="h-3 w-3 shrink-0" strokeWidth={1.75} aria-hidden />
      <span className="truncate">{title}</span>
      {detail && <span className="shrink-0 opacity-70">· {detail}</span>}
    </span>
  );
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** Animated "…" while the assistant thinks — the static three dots read as a frozen /
 *  broken chat (owner feedback). Three staggered bouncing dots instead. */
/** The `‹ 2/3 ›` branch switcher (ADR-0072). Renders ONLY where the conversation
 *  actually forked (`total > 1`) — a normal chat never shows it. */
function BranchSwitcher({
  branch,
  onGo,
}: {
  branch: MessageBranch;
  onGo: (delta: number) => void;
}) {
  if (branch.total < 2) return null;
  return (
    <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
      <button
        type="button"
        aria-label="Ramo precedente"
        disabled={branch.index === 0}
        onClick={() => onGo(-1)}
        className="rounded p-0.5 transition hover:text-foreground disabled:opacity-30"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="tabular-nums">
        {branch.index + 1}/{branch.total}
      </span>
      <button
        type="button"
        aria-label="Ramo successivo"
        disabled={branch.index === branch.total - 1}
        onClick={() => onGo(1)}
        className="rounded p-0.5 transition hover:text-foreground disabled:opacity-30"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 py-1.5" role="status" aria-label="…">
      {[0, 160, 320].map((d) => (
        <span
          key={d}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-muted-foreground/70"
          style={{ animationDelay: `${d}ms` }}
        />
      ))}
    </span>
  );
}

// A tool NAME → a human "what I'm doing" verb, shown WHILE the tool runs (the Claude-style
// "retropensiero"): a plain loader gives no sense of progress, this narrates it. Inline IT
// (this chat subsystem keeps its strings inline — no locale entries), fallback = generic.
const ACTIVITY_LABELS: Record<string, string> = {
  code_execution: "Eseguo il codice…",
  compute: "Elaboro i dati…",
  query_table: "Consulto i dati…",
  query_projection: "Consulto i dati…",
  query_dataset: "Consulto i dati…",
  run_metric: "Calcolo…",
  list_tables: "Esamino i dati…",
  describe_dataset: "Esamino i dati…",
  read_uploaded_file: "Leggo il file…",
  web_fetch: "Cerco sul web…",
  create_row: "Salvo i dati…",
  update_row: "Salvo i dati…",
  delete_row: "Elimino i dati…",
};

const activityLabel = (name: string): string => ACTIVITY_LABELS[name] ?? "Sto lavorando…";

/** The live "retropensiero" line while the assistant works — animated dots + a narration
 *  of the current step, so the gap between a tool firing and its result feels alive. */
function ActivityLine({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-2 py-0.5 text-sm text-muted-foreground">
      <TypingDots />
      <span>{label}</span>
    </span>
  );
}

/** The tool CHIPS, tucked behind a DISCREET disclosure (owner: keep them, but hidden — the
 *  end-user doesn't need the badges). The visible answer content (chart / proof rows /
 *  files) renders separately, ABOVE; this is just the "what it did" audit trail on demand. */
function ToolActivity({ tools }: { tools: ToolChipItem[] }) {
  const label = tools.length === 1 ? "1 passaggio" : `${tools.length} passaggi`;
  return (
    <details className="group/act mt-2">
      <summary className="flex w-fit cursor-pointer list-none items-center gap-1 text-[11px] text-muted-foreground/70 transition hover:text-muted-foreground">
        <Sparkles className="h-3 w-3" strokeWidth={1.75} aria-hidden />
        <span>{label}</span>
        <ChevronRight className="h-3 w-3 transition group-open/act:rotate-90" aria-hidden />
      </summary>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {tools.map((tl) => (
          <ToolChip key={tl.id} name={tl.name} p={tl.preview ?? {}} />
        ))}
      </div>
    </details>
  );
}

/** Does this tool call have VISIBLE result content (a chart or proof rows) to render
 *  above the answer? (A pure read/write with no rows only lives as a chip.) */
const hasToolResult = (tl: ToolChipItem) =>
  !!tl.result?.chart || (tl.result?.rows?.length ?? 0) > 0;

/** File-type presentation for a produced download: a lucide icon + a friendly label +
 *  a theme-token tint (concrete class strings so Tailwind's JIT emits them). */
const FILE_KIND_TONE: Record<string, string> = {
  warning: "bg-warning/15 text-warning",
  danger: "bg-danger/15 text-danger",
  success: "bg-success/15 text-success",
  info: "bg-info/15 text-info",
  accent: "bg-accent/15 text-accent",
};
const FILE_KINDS: Record<string, { label: string; tone: string; Icon: LucideIcon }> = {
  pptx: { label: "PowerPoint", tone: "warning", Icon: Presentation },
  ppt: { label: "PowerPoint", tone: "warning", Icon: Presentation },
  pdf: { label: "PDF", tone: "danger", Icon: FileText },
  xlsx: { label: "Excel", tone: "success", Icon: FileSpreadsheet },
  xls: { label: "Excel", tone: "success", Icon: FileSpreadsheet },
  csv: { label: "CSV", tone: "success", Icon: FileSpreadsheet },
  docx: { label: "Word", tone: "info", Icon: FileText },
  doc: { label: "Word", tone: "info", Icon: FileText },
  png: { label: "PNG", tone: "accent", Icon: ImageIcon },
  jpg: { label: "JPG", tone: "accent", Icon: ImageIcon },
  jpeg: { label: "JPEG", tone: "accent", Icon: ImageIcon },
  gif: { label: "GIF", tone: "accent", Icon: ImageIcon },
  webp: { label: "WebP", tone: "accent", Icon: ImageIcon },
  svg: { label: "SVG", tone: "accent", Icon: ImageIcon },
};
function fileKind(name: string): { label: string; tone: string; Icon: LucideIcon } {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return FILE_KINDS[ext] ?? { label: ext ? ext.toUpperCase() : "File", tone: "accent", Icon: File };
}

/** A produced file rendered as a COMPACT attachment chip (icon tile + name + type +
 *  a ghost download button). Sized to its CONTENT (`w-fit`), not the full bubble width —
 *  the old full-width `max-w-xs` card dwarfed a one-line answer (owner feedback). */
function DownloadFileCard({ name, onDownload }: { name: string; onDownload: () => void }) {
  const { t } = useTranslation();
  const base = name.split("/").pop() ?? name;
  const kind = fileKind(base);
  return (
    <button
      type="button"
      onClick={onDownload}
      title={t("chat.download", "Scarica")}
      className="group flex w-fit max-w-[15rem] items-center gap-2 rounded-xl border border-border bg-surface py-1 pl-1 pr-1.5 text-left transition hover:border-accent/40 hover:bg-elevated"
    >
      <span
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${FILE_KIND_TONE[kind.tone]}`}
      >
        <kind.Icon className="h-4 w-4" strokeWidth={1.75} />
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate text-[13px] font-medium text-foreground">{base}</span>
        <span className="block text-[11px] text-muted-foreground">{kind.label}</span>
      </span>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition group-hover:bg-accent/10 group-hover:text-accent">
        <Download className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
    </button>
  );
}

const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"];
const isImageFile = (name: string) =>
  IMAGE_EXTS.includes((name.split(".").pop() ?? "").toLowerCase());

/** A file a tool PRODUCED, addressable EITHER by a signed download `token` (the immutable-blob
 *  path — the current default) OR, on a legacy persisted thread, only by its workspace `name`. */
type ProducedFile = { name: string; token?: string };

/** Collect the produced files of an assistant turn from its tool results — the NEW token-carrying
 *  `files` (deduped by token) plus the LEGACY name-only `output_files` (deduped by name, and
 *  skipped when the same name already arrived with a token). */
function producedFilesOf(tools: { result?: VoiceToolRows }[]): ProducedFile[] {
  const out: ProducedFile[] = [];
  const seenTokens = new Set<string>();
  const seenNames = new Set<string>();
  for (const tl of tools) {
    for (const f of tl.result?.files ?? []) {
      if (seenTokens.has(f.token)) continue;
      seenTokens.add(f.token);
      seenNames.add(f.name);
      out.push({ name: f.name, token: f.token });
    }
  }
  for (const tl of tools) {
    for (const name of tl.result?.output_files ?? []) {
      if (seenNames.has(name)) continue;
      seenNames.add(name);
      out.push({ name });
    }
  }
  return out;
}

/** A produced IMAGE shown INLINE (the real thing — a QR, a chart, a rendered page) with a
 *  hover download, instead of a file card. Fetches the bytes once via `fetchBlob` (by signed
 *  token for the immutable-blob path, or by workspace path for legacy threads) → object URL;
 *  degrades to `fallback` (the compact card) on any error, so a broken/expired file still
 *  downloads. `depKey` re-fetches when the underlying file identity changes. */
function ImagePreview({
  fetchBlob,
  name,
  depKey,
  onDownload,
  fallback,
}: {
  fetchBlob: () => Promise<Blob>;
  name: string;
  depKey: string;
  onDownload: () => void;
  fallback: React.ReactNode;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    let obj: string | null = null;
    void (async () => {
      try {
        const blob = await fetchBlob();
        if (!alive) return;
        obj = URL.createObjectURL(blob);
        setUrl(obj);
      } catch {
        if (alive) setFailed(true);
      }
    })();
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  if (failed) return <>{fallback}</>;
  const base = name.split("/").pop() ?? name;
  return (
    <figure className="w-fit">
      <button
        type="button"
        onClick={onDownload}
        title={t("chat.download", "Scarica")}
        className="group relative block overflow-hidden rounded-xl border border-border bg-surface transition hover:border-accent/40"
      >
        {url ? (
          <img
            src={url}
            alt={base}
            className="block max-h-56 max-w-[15rem] object-contain"
            loading="lazy"
          />
        ) : (
          <span className="flex h-32 w-40 items-center justify-center text-muted-foreground">
            <ImageIcon className="h-6 w-6 animate-pulse" strokeWidth={1.5} />
          </span>
        )}
        <span className="pointer-events-none absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-lg bg-background/85 text-foreground opacity-0 shadow-sm transition group-hover:opacity-100">
          <Download className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
      </button>
      <figcaption className="mt-1 max-w-[15rem] truncate text-[11px] text-muted-foreground">
        {base}
      </figcaption>
    </figure>
  );
}

/** The "what I actually read" table — the rows a read tool returned, so the user
 *  can verify the answer against the real data (anti-fabrication). The query
 *  itself is NEVER shown; the backend echoes only the (RLS-scoped, capped) rows. */
/** The raw rows, COLLAPSED by default (answer-first UX): the answer leads with prose +
 *  chart, and the grezzo data is one click away. It's still the anti-fabrication proof
 *  (ADR-0028) — never removed, just tucked behind a `<details>` so an operator sees the
 *  answer, not a table dump, unless they open it. */
function ToolRows({ r }: { r: VoiceToolRows }) {
  const { t } = useTranslation();
  const rows = r.rows ?? [];
  const columns = r.columns ?? [];
  const extra = r.total != null ? r.total - rows.length : 0;
  const count = r.total ?? rows.length;
  return (
    <details className="mt-1">
      <summary className="cursor-pointer select-none list-none text-[11px] text-muted-foreground marker:content-none hover:text-foreground">
        ▸ {t("chat.showData", "Dati")} · {count} {t("chat.rowsUnit", "righe")}
      </summary>
      <div className="mt-1 max-w-full overflow-x-auto rounded-md border border-border/60">
        <table className="w-full border-collapse text-[11px]">
          <thead>
            <tr className="border-b border-border/60 bg-elevated/60">
              {columns.map((c) => (
                <th
                  key={c}
                  className="whitespace-nowrap px-1.5 py-1 text-left font-medium text-muted-foreground"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-border/40 last:border-0">
                {columns.map((c) => (
                  <td key={c} className="whitespace-nowrap px-1.5 py-1 text-foreground">
                    {fmtCell(row[c])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {extra > 0 && (
          <div className="px-1.5 py-1 text-[11px] text-muted-foreground">
            {t("chat.rowsMore", "… e altri")} {extra}
          </div>
        )}
      </div>
    </details>
  );
}

/** A single record rendered as a readable key/value CARD (not a 1-row table) — the
 *  ~70% "trovami questa cosa" case an operator hits, shown like a contact/record card. */
function RecordCard({ r }: { r: VoiceToolRows }) {
  const row = r.rows?.[0] ?? {};
  return (
    <div className="mt-1 rounded-md border border-border/60 bg-elevated/40 px-2.5 py-2 text-xs">
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {(r.columns ?? []).map((c) => (
          <React.Fragment key={c}>
            <dt className="whitespace-nowrap font-medium text-muted-foreground">{c}</dt>
            <dd className="break-words text-foreground">{fmtCell(row[c])}</dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

export function ChatAssistant({
  appSlug,
  appName,
  sideOffset = false,
  standalone = false,
  onClose,
  onExpand,
  switcher,
}: {
  appSlug: string;
  appName?: string;
  /** Shift the launcher left so it doesn't sit on top of the voice orb when
   *  BOTH interfaces are enabled for the app. */
  sideOffset?: boolean;
  /** ADR-0049 Slice D — render as an AUTONOMOUS full-page surface (ChatGPT-style)
   *  instead of the floating launcher+dock: no FAB, always open, fills its parent.
   *  The host page owns the viewport + theme; this just fills it. */
  standalone?: boolean;
  /** What the close (✕) control does. In the widget it collapses the dock; in a
   *  standalone surface the host passes a navigate-back (there's nothing to collapse). */
  onClose?: () => void;
  /** When set (docked widget only), shows an "expand" control that opens the
   *  autonomous full-page chat surface (ADR-0049 Slice D). */
  onExpand?: () => void;
  /** Standalone-only: an interface switcher (Web ⇄ Chat ⇄ Voce) rendered in the
   *  main header, so the user can hop between the app's front doors (ADR-0049). */
  switcher?: React.ReactNode;
}) {
  const { t } = useTranslation();
  // Standalone surfaces boot already-open (there is no launcher to click).
  const [open, setOpen] = React.useState(standalone);
  const closePanel = onClose ?? (() => setOpen(false));
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);
  const historyRef = React.useRef<{ role: "user" | "assistant"; text: string }[]>([]);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const toolSeqRef = React.useRef(0);
  // ADR-0028 server-side memory: the persisted thread this conversation is bound to
  // (null = a fresh, not-yet-saved chat — the thread row is created lazily on first
  // send). The thread list + rehydration make the transcript survive a page refresh.
  const [threadId, setThreadId] = React.useState<string | null>(null);
  const [threads, setThreads] = React.useState<AssistantSessionSummary[]>([]);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  /** Which user message is being re-worded (ADR-0072 edit), + its draft text. */
  const [editingIdx, setEditingIdx] = React.useState<number | null>(null);
  const [editDraft, setEditDraft] = React.useState("");
  const bootedRef = React.useRef(false);
  // Files STAGED for the next message (ADR-0034) — uploaded to the thread's sandbox
  // workspace on pick, shown above the composer, then moved INTO the user bubble on send.
  const [pending, setPending] = React.useState<AttachedFile[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [attachError, setAttachError] = React.useState<string | null>(null);

  // Keep the transcript pinned to the latest message as it streams.
  React.useEffect(() => {
    // `scrollTo` is absent in jsdom (tests) — call it optionally.
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  // Cancel an in-flight turn if the widget unmounts.
  React.useEffect(() => () => abortRef.current?.abort(), []);

  // ADR-0034: what the resolved chat model can SEE (images/PDF natively) — the vision badge.
  // Fetched once the panel opens; non-critical (a failure just hides the badge).
  const [vision, setVision] = React.useState<ModelVision | null>(null);
  React.useEffect(() => {
    if (!open) return;
    void portalApi
      .assistantCapabilities(appSlug, "chat")
      .then(setVision)
      .catch(() => setVision(null));
  }, [open, appSlug]);

  // Suggested opening questions, grounded in the surfaces this role can read (Wave-1
  // empty-state). Fetched once on open; a failure just falls back to the generic hint.
  const [starters, setStarters] = React.useState<PortalAssistantStarter[]>([]);
  React.useEffect(() => {
    if (!open) return;
    void portalApi
      .assistantStarters(appSlug, "chat")
      .then((r) => setStarters(r.starters))
      .catch(() => setStarters([]));
  }, [open, appSlug]);

  const loadThreads = React.useCallback(async () => {
    try {
      setThreads(await portalApi.listAssistantSessions(appSlug, "chat"));
    } catch {
      /* memory is best-effort UI — a load failure just hides the history list */
    }
  }, [appSlug]);

  /** Load the thread's ACTIVE PATH from the server and render it (ADR-0072). The
   *  server owns the tree — the client just renders the branch it returns — so this is
   *  ALSO the reconcile after a turn: it swaps the optimistic bubbles for the persisted
   *  nodes, which is what gives every message its id + branch counter. */
  const loadPath = React.useCallback(
    async (id: string, minLength = 0) => {
      const msgs = await portalApi.getAssistantMessages(appSlug, id);
      // Persistence is BEST-EFFORT server-side, so a reconcile can come back WITHOUT the
      // turn that just streamed (persist failed / the thread isn't ours). Adopting that
      // would erase the answer in front of the user — so a path shorter than what we're
      // showing is REFUSED and the streamed view stands. `minLength` is 0 for a plain
      // open (there's nothing on screen to protect).
      if (msgs.length < minLength) return null;
      // Rebuild the RICH transcript (ADR-0071 F1b): assistant rows carry their
      // persisted tool cards, so chips + proof tables + charts replay on reopen.
      const rehydrated: ChatMessage[] = msgs.map((m) => ({
        id: m.id,
        parentId: m.parent_id,
        branch: m.branch,
        role: m.role,
        text: m.text,
        tools: m.tools?.length
          ? m.tools.map((tl, i) => ({
              id: `h${m.id}-${i}`,
              name: tl.name ?? "",
              preview: tl.preview,
              result: tl.result,
            }))
          : undefined,
      }));
      setMessages(rehydrated);
      // The model history stays TEXT-ONLY (cards are UI replay, never fed back).
      historyRef.current = rehydrated.map((m) => ({ role: m.role, text: m.text }));
      return rehydrated;
    },
    [appSlug],
  );

  const openThread = React.useCallback(
    async (id: string) => {
      setAttachError(null);
      try {
        await loadPath(id);
        setThreadId(id);
      } catch {
        /* ignore — a stale/forbidden id just won't open */
      }
      setPending([]); // staged files belong to the previous thread, not the reopened one
      setHistoryOpen(false);
    },
    [loadPath],
  );

  const newThread = React.useCallback(() => {
    setMessages([]);
    historyRef.current = [];
    setThreadId(null);
    setPending([]);
    setAttachError(null);
    setHistoryOpen(false);
  }, []);

  const onPickFiles = React.useCallback(
    async (files: File[]) => {
      if (uploading || busy) return;
      setAttachError(null);
      setUploading(true);
      // Bind (or lazily create) the thread so the files land in ITS workspace.
      let tid = threadId;
      if (!tid) {
        try {
          tid = (await portalApi.createAssistantSession(appSlug, "chat")).id;
          setThreadId(tid);
        } catch {
          tid = null;
        }
      }
      if (!tid) {
        setAttachError(t("chat.uploadFailed", "Caricamento file non riuscito."));
        setUploading(false);
        return;
      }
      try {
        const res = await portalApi.uploadAssistantFiles(appSlug, tid, files);
        setPending((cur) => {
          const seen = new Set(cur.map((f) => f.name));
          // Map (idx aligns to the picked Files for previews) THEN drop dupes.
          const added = res.uploaded
            .map((f, idx) => ({
              name: f.name,
              previewUrl: files[idx] ? imagePreviewUrl(files[idx]) : undefined,
            }))
            .filter((f) => !seen.has(f.name));
          return [...cur, ...added];
        });
        void loadThreads();
      } catch (e) {
        const status = (e as { status?: number }).status ?? 0;
        setAttachError(
          status === 503
            ? t("chat.uploadSandboxOff", "La sandbox non è configurata su questa istanza.")
            : status === 422
              ? t("chat.uploadRejected", "File non caricato: troppo grande o troppi file.")
              : t("chat.uploadFailed", "Caricamento file non riuscito."),
        );
      } finally {
        setUploading(false);
      }
    },
    [appSlug, busy, uploading, threadId, t, loadThreads],
  );

  const removeThread = React.useCallback(
    async (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await portalApi.deleteAssistantSession(appSlug, id);
      } catch {
        /* ignore */
      }
      if (id === threadId) newThread();
      void loadThreads();
    },
    [appSlug, threadId, newThread, loadThreads],
  );

  // On first open: load the thread list and RESUME the most-recent conversation
  // (sorted newest-first by the API), so reopening the chat after a refresh feels
  // continuous instead of blank. A user can start fresh via the "new chat" button.
  React.useEffect(() => {
    if (!open || bootedRef.current) return;
    bootedRef.current = true;
    void (async () => {
      let list: AssistantSessionSummary[] = [];
      try {
        list = await portalApi.listAssistantSessions(appSlug, "chat");
      } catch {
        return;
      }
      setThreads(list);
      if (list.length > 0) void openThread(list[0].id);
    })();
  }, [open, appSlug, openThread]);

  const send = React.useCallback(
    async (text: string, fork?: ForkTarget) => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setBusy(true);
      // Push the user line (with its staged attachments) + an empty assistant line we
      // stream into (optimistic — the bubble appears immediately). Clear the composer tray.
      // A FORK first TRUNCATES the visible path to the node it hangs off (ADR-0072): the
      // messages after it belong to the branch being left, and the server won't return
      // them either — so showing them until the reconcile would be a lie.
      const atts = pending.length ? pending : undefined;
      setMessages((cur) => {
        const kept = fork ? cur.slice(0, fork.keepUpTo) : cur;
        return [
          ...kept,
          { role: "user", text, attachments: atts },
          { role: "assistant", text: "" },
        ];
      });
      setPending([]);
      // The model's history must match the branch too — the server recomputes it from
      // `parent_id` anyway (that's authoritative); this keeps the stateless fallback honest.
      const priorHistory = fork
        ? historyRef.current.slice(0, fork.keepUpTo)
        : historyRef.current.slice();
      historyRef.current = [...priorHistory, { role: "user", text }];
      let acc = "";
      // Bind (or lazily create) the persisted thread so the turn is saved server-side
      // and reloadable. A create failure degrades to a stateless turn (no thread_id).
      let tid = threadId;
      if (!tid) {
        try {
          tid = (await portalApi.createAssistantSession(appSlug, "chat")).id;
          setThreadId(tid);
        } catch {
          tid = null;
        }
      }

      const patchAssistant = (patch: Partial<ChatMessage>) =>
        setMessages((cur) => {
          const next = cur.slice();
          // The assistant placeholder is always the last message.
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].role === "assistant") {
              next[i] = { ...next[i], ...patch };
              break;
            }
          }
          return next;
        });

      await streamChatTurn(
        appSlug,
        {
          text,
          history: priorHistory,
          thread_id: tid ?? undefined,
          // ADR-0072 — the ONE field behind fork / edit / regenerate.
          parent_id: fork?.parentId,
          // Pass THIS message's attachments so a multimodal model gets them natively.
          attachments: atts?.map((a) => a.name),
        },
        (ev: VoiceEvent) => {
          if (ev.type === "delta") {
            acc += ev.text;
            patchAssistant({ text: acc, tool: undefined });
          } else if (ev.type === "tool") {
            patchAssistant({ tool: activityLabel(ev.name) });
          } else if (ev.type === "tool_result") {
            // Record the completed tool call as a chip on the assistant turn (the
            // chat twin of the voice HUD) — so the user SEES what was read/written.
            const id = `t${(toolSeqRef.current += 1)}`;
            setMessages((cur) => {
              const next = cur.slice();
              for (let i = next.length - 1; i >= 0; i--) {
                if (next[i].role === "assistant") {
                  next[i] = {
                    ...next[i],
                    tool: undefined,
                    tools: [
                      ...(next[i].tools ?? []),
                      { id, name: ev.name, preview: ev.preview, result: ev.result },
                    ],
                  };
                  break;
                }
              }
              return next;
            });
          } else if (ev.type === "final") {
            acc = ev.text || acc;
            patchAssistant({ text: acc, tool: undefined });
          } else if (ev.type === "error") {
            patchAssistant({
              text: `${t("chat.error", "Sorry, something went wrong.")} (${ev.message})`,
              tool: undefined,
              error: true,
            });
          }
        },
        ctrl.signal,
      );
      historyRef.current.push({ role: "assistant", text: acc });
      patchAssistant({ tool: undefined });
      setBusy(false);
      // Reconcile with the server's ACTIVE PATH: the optimistic bubbles have no ids, so
      // without this the new turn carries no branch counter and can't be forked from.
      // Best-effort — a failed (or turn-less) reload just leaves the streamed view.
      if (tid) {
        try {
          await loadPath(tid, priorHistory.length + 2);
        } catch {
          /* keep the streamed view */
        }
      }
      // Refresh the thread list so the (newly-created) conversation + its derived
      // title surface in the history panel.
      void loadThreads();
    },
    [appSlug, t, threadId, loadThreads, loadPath, pending],
  );

  // A clickable chip (empty-state starter OR follow-up) sends its prompt as a turn.
  const runChip = React.useCallback(
    (prompt: string) => {
      if (busy) return;
      void send(prompt);
    },
    [busy, send],
  );

  // --- the conversation TREE (ADR-0072) ------------------------------------
  // Fork / edit / regenerate are ONE call — `send(text, {parentId, keepUpTo})`. Which
  // node you hang off is the whole difference; the server derives the rest from its role.

  /** Re-ask the SAME question: a sibling answer under the user message (`idx`). */
  const regenerate = React.useCallback(
    (idx: number) => {
      const userMsg = messages[idx - 1];
      if (busy || !userMsg?.id || userMsg.role !== "user") return;
      void send(userMsg.text, { parentId: userMsg.id, keepUpTo: idx - 1 });
    },
    [busy, messages, send],
  );

  /** Ask a DIFFERENT question in the same place: a sibling of the user message — so it
   *  hangs off that message's PARENT (the previous answer), or the root when it's first. */
  const submitEdit = React.useCallback(
    (idx: number, text: string) => {
      const msg = messages[idx];
      if (busy || !msg?.id || msg.role !== "user" || !text.trim()) return;
      setEditingIdx(null);
      void send(text.trim(), { parentId: msg.parentId ?? ROOT_PARENT, keepUpTo: idx });
    },
    [busy, messages, send],
  );

  /** Move the thread's cursor to a sibling — the WHOLE branch switch: nothing is copied
   *  or deleted server-side, so the abandoned branch comes back verbatim. */
  const switchBranch = React.useCallback(
    async (idx: number, delta: number) => {
      const msg = messages[idx];
      if (busy || !threadId || !msg?.branch) return;
      const pick = msg.branch.siblings[msg.branch.index + delta];
      if (!pick) return;
      try {
        await portalApi.setAssistantActiveLeaf(appSlug, threadId, pick);
        await loadPath(threadId);
      } catch {
        setAttachError(t("chat.branchFailed", "Non sono riuscito a cambiare ramo."));
      }
    },
    [appSlug, busy, threadId, messages, loadPath, t],
  );

  // Download a file the assistant PRODUCED. The immutable-blob path carries a signed TOKEN
  // (always the RIGHT file even after the workspace reused the name); a legacy persisted
  // thread carries only the workspace path (downloaded by name).
  const downloadFile = React.useCallback(
    async (file: ProducedFile) => {
      try {
        if (file.token) {
          await portalApi.downloadAssistantFileByToken(
            appSlug,
            file.token,
            file.name.split("/").pop() ?? file.name,
          );
        } else if (threadId) {
          await portalApi.downloadAssistantFile(appSlug, threadId, file.name);
        } else {
          return;
        }
      } catch {
        setAttachError(t("chat.downloadFailed", "Download non riuscito (file scaduto?)."));
      }
    },
    [appSlug, threadId, t],
  );

  // Contextual follow-up chips shown under a completed answer (Wave-1) — nudges that
  // guide the conversation. Derived from the last assistant turn: offer a chart when it
  // read rows but drew none, and always the generic deepen/summarise moves.
  const followUps = React.useMemo<string[]>(() => {
    if (busy || messages.length === 0) return [];
    const last = messages[messages.length - 1];
    if (last.role !== "assistant" || !last.text || last.error) return [];
    const chips: string[] = [];
    const hasRows = last.tools?.some((tl) => (tl.result?.rows?.length ?? 0) > 0);
    const hasChart = last.tools?.some((tl) => tl.result?.chart);
    if (hasRows && !hasChart) chips.push(t("chat.followChart", "Fai un grafico"));
    chips.push(t("chat.followDeeper", "Approfondisci"));
    chips.push(t("chat.followSummary", "Riepiloga in breve"));
    return chips;
  }, [busy, messages, t]);

  const submit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    // The textarea auto-grows on input; reset it to one row after sending.
    if (inputRef.current) inputRef.current.style.height = "auto";
    void send(text);
  };

  // Enter sends; Shift+Enter inserts a newline (multi-line message).
  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) submit(e);
  };

  // The conversation list (named threads, ADR-0028 memory). In `standalone` it's
  // the ChatGPT-style LEFT RAIL (always visible on desktop); in the widget it's a
  // full-panel overlay toggled by the History button.
  const threadListEl = (
    <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
      <button
        type="button"
        onClick={newThread}
        className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-accent transition hover:bg-elevated"
      >
        <Plus className="h-4 w-4" /> {t("chat.new", "New chat")}
      </button>
      {threads.length === 0 && (
        <p className="px-3 py-4 text-center text-xs text-muted-foreground">
          {t("chat.noThreads", "No saved conversations yet.")}
        </p>
      )}
      {threads.map((th) => (
        <div
          key={th.id}
          className={`group flex items-center rounded-lg pr-1 transition hover:bg-elevated ${
            th.id === threadId ? "bg-elevated" : ""
          }`}
        >
          <button
            type="button"
            onClick={() => void openThread(th.id)}
            className="min-w-0 flex-1 truncate px-3 py-2 text-left text-sm text-foreground"
          >
            {th.title || t("chat.untitled", "New conversation")}
          </button>
          <button
            type="button"
            onClick={(e) => void removeThread(th.id, e)}
            aria-label={t("chat.deleteThread", "Delete conversation")}
            className="shrink-0 rounded p-1.5 text-muted-foreground opacity-0 transition hover:text-danger group-hover:opacity-100"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );

  const transcriptEl = (
    <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl space-y-3 px-4 py-4">
        {messages.length === 0 && (
          <div className="mt-8 flex flex-col items-center gap-3 text-center">
            <p className="text-sm text-muted-foreground">
              {t("chat.empty", "Ask me anything about your data.")}
            </p>
            {/* Wave-1 empty-state: grounded starter chips so the first question isn't a
                blank box. Fall back to just the hint when the role has no readable surface. */}
            {starters.length > 0 && (
              <div className="flex max-w-md flex-wrap justify-center gap-1.5">
                {starters.map((s) => (
                  <button
                    key={s.prompt}
                    type="button"
                    onClick={() => runChip(s.prompt)}
                    className="rounded-full border border-border/70 bg-surface px-3 py-1.5 text-xs text-foreground transition hover:border-accent hover:bg-elevated"
                  >
                    {s.prompt}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={m.id ?? i}
            className={
              m.role === "user"
                ? "group flex flex-col items-end gap-1.5"
                : "group flex flex-col items-start gap-1.5"
            }
          >
            {m.role === "user" && m.attachments && m.attachments.length > 0 && (
              <div className="flex max-w-[85%] justify-end">
                <AttachmentChips files={m.attachments} />
              </div>
            )}
            {/* Re-wording a question (ADR-0072 edit): sending it FORKS — the old
                question and its answer stay on their branch, reachable via the switcher. */}
            {m.role === "user" && editingIdx === i ? (
              <div className="flex w-[85%] flex-col gap-1.5">
                <textarea
                  value={editDraft}
                  autoFocus
                  rows={2}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setEditingIdx(null);
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      submitEdit(i, editDraft);
                    }
                  }}
                  className="w-full resize-none rounded-2xl border border-border bg-surface px-3 py-2 text-sm text-foreground outline-none focus:border-accent"
                />
                <div className="flex justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => setEditingIdx(null)}
                    className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition hover:text-foreground"
                  >
                    {t("chat.cancel", "Annulla")}
                  </button>
                  <button
                    type="button"
                    onClick={() => submitEdit(i, editDraft)}
                    disabled={!editDraft.trim()}
                    className="inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs text-accent-foreground transition disabled:opacity-40"
                  >
                    <Check className="h-3 w-3" />
                    {t("chat.sendEdit", "Invia")}
                  </button>
                </div>
              </div>
            ) : (
              (m.role !== "user" || m.text) && (
                <div
                  className={`max-w-[85%] break-words rounded-2xl px-3 py-2 text-sm ${
                    m.role === "user"
                      ? "bg-accent text-accent-foreground"
                      : m.error
                        ? "bg-danger/10 text-danger"
                        : "bg-elevated text-foreground"
                  }`}
                >
                  {/* RESULT CONTENT (answer-first) — chart / proof rows / record card render
                    ABOVE the prose. The tool CHIPS themselves are hidden in a discreet
                    disclosure below (owner: the end-user doesn't need the badges). */}
                  {m.role === "assistant" && m.tools && m.tools.some(hasToolResult) && (
                    <div className="mb-2 space-y-1.5">
                      {m.tools.map((tl) => (
                        <React.Fragment key={tl.id}>
                          {/* Agent-emitted chart (ADR-0071 F1a): the spec is shape-only,
                          the data is the SAME masked rows the proof table shows. The chart
                          LEADS (visual), the raw table is collapsed below it. */}
                          {tl.result?.chart && (tl.result.rows?.length ?? 0) > 0 && (
                            <ResultChart spec={tl.result.chart} rows={tl.result.rows ?? []} />
                          )}
                          {tl.result?.rows &&
                            tl.result.rows.length > 0 &&
                            (tl.result.rows.length === 1 && !tl.result.chart ? (
                              <RecordCard r={tl.result} />
                            ) : (
                              <ToolRows r={tl.result} />
                            ))}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {/* Assistant answers may use light markdown; user text stays plain. While
                    still working, an animated "retropensiero" line narrates the current step
                    (Claude-style) instead of a mute loader. */}
                  {m.role === "assistant" ? (
                    m.text ? (
                      <ChatMarkdown text={m.text} />
                    ) : (
                      <ActivityLine label={m.tool ?? t("chat.thinking", "Sto pensando…")} />
                    )
                  ) : (
                    <span className="whitespace-pre-wrap">{m.text}</span>
                  )}
                  {/* Files the assistant PRODUCED — rendered BELOW the answer, and ONLY once
                    the answer text is in (`m.text`): showing the download BEFORE the prose
                    confused ("il file appare prima della risposta"). Deduped across tools. */}
                  {m.role === "assistant" &&
                    m.text &&
                    (() => {
                      const files = producedFilesOf(m.tools ?? []);
                      if (files.length === 0) return null;
                      return (
                        <div className="mt-2.5 flex flex-col items-start gap-2">
                          {files.map((f) => {
                            const key = f.token ?? f.name;
                            // The immutable-blob path fetches by signed token; a legacy thread
                            // (no token) still fetches by its workspace path.
                            const canFetch = f.token != null || threadId != null;
                            const fetchBlob = () =>
                              f.token
                                ? portalApi.assistantFileBlobByToken(appSlug, f.token)
                                : portalApi.assistantFileBlob(appSlug, threadId!, f.name);
                            const card = (
                              <DownloadFileCard name={f.name} onDownload={() => void downloadFile(f)} />
                            );
                            return isImageFile(f.name) && canFetch ? (
                              <ImagePreview
                                key={key}
                                fetchBlob={fetchBlob}
                                name={f.name}
                                depKey={key}
                                onDownload={() => void downloadFile(f)}
                                fallback={card}
                              />
                            ) : (
                              <React.Fragment key={key}>{card}</React.Fragment>
                            );
                          })}
                        </div>
                      );
                    })()}
                  {/* The tool CHIPS — tucked behind a discreet "N passaggi ▸" disclosure once
                    the answer is in (owner: keep them, but hidden from the end-user). */}
                  {m.role === "assistant" && m.text && m.tools && m.tools.length > 0 && (
                    <ToolActivity tools={m.tools} />
                  )}
                </div>
              )
            )}
            {/* The tree affordances (ADR-0072). They need a PERSISTED node (an
                optimistic bubble has no id yet) and appear on hover — a conversation
                that never forks looks exactly as it did. */}
            {m.id && editingIdx !== i && (
              <div
                className={`flex items-center gap-1 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100 ${
                  m.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {m.branch && (
                  <BranchSwitcher branch={m.branch} onGo={(d) => void switchBranch(i, d)} />
                )}
                {m.role === "user" ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setEditingIdx(i);
                      setEditDraft(m.text);
                    }}
                    className="rounded p-0.5 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                    aria-label={t("chat.editMessage", "Modifica messaggio")}
                    title={t("chat.editMessage", "Modifica messaggio")}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  !m.error && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => regenerate(i)}
                      className="rounded p-0.5 text-muted-foreground transition hover:text-foreground disabled:opacity-30"
                      aria-label={t("chat.regenerate", "Rigenera risposta")}
                      title={t("chat.regenerate", "Rigenera risposta")}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        ))}
        {/* Wave-1 contextual follow-up chips: guide the conversation after an answer. */}
        {followUps.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {followUps.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => runChip(f)}
                className="rounded-full border border-border/70 bg-surface px-3 py-1 text-xs text-muted-foreground transition hover:border-accent hover:text-foreground"
              >
                {f}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const composerEl = (
    <div className="border-t border-border px-4 py-2.5">
      <div className="mx-auto w-full max-w-3xl">
        {attachError && <p className="mb-1.5 text-xs text-danger">{attachError}</p>}
        {pending.length > 0 && (
          <div className="mb-1.5">
            <AttachmentChips files={pending} />
          </div>
        )}
        {/* ChatGPT-style rounded composer: attach + model-vision icon + borderless
            input + send inside one pill, so the meta controls line up on the left
            instead of a badge floating above. */}
        <form
          onSubmit={submit}
          className="flex items-end gap-1 rounded-2xl border border-border bg-background px-2 py-1 shadow-sm transition focus-within:ring-2 focus-within:ring-ring"
        >
          {/* The attach affordance shows ONLY when the resolved assistant can READ uploads
              (its agent carries the `uploads`/`code_exec` capability). The system assistant
              has none → no button, so the user is never offered an upload the model can't
              read (ADR-0034 honesty gap closed; owner's rule "uploads è un tool di sistema"). */}
          {vision?.uploads && (
            <AttachButton
              onPick={(f) => void onPickFiles(f)}
              uploading={uploading}
              disabled={busy}
              label={t("chat.attach", "Allega file")}
            />
          )}
          {vision && <ModelVisionBadge caps={vision} iconOnly />}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onComposerKeyDown}
            onInput={(e) => {
              // Grow with content up to a cap (then scroll internally).
              const el = e.currentTarget;
              el.style.height = "auto";
              el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
            }}
            rows={1}
            placeholder={t("chat.placeholder", "Type a message…")}
            className="max-h-40 min-w-0 flex-1 resize-none bg-transparent px-1.5 py-1.5 text-sm text-foreground outline-none"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            aria-label={t("chat.send", "Send")}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground transition hover:opacity-90 disabled:opacity-40"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );

  // Header — the History toggle is desktop-hidden on the standalone surface
  // (the persistent left rail replaces it there; it survives for mobile).
  const headerEl = (
    <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
      {standalone && switcher ? (
        // The app name lives in the left rail already — give the header slot to the
        // interface switcher (Web ⇄ Chat ⇄ Voce).
        switcher
      ) : (
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-accent" strokeWidth={2} />
          <span className="text-sm font-semibold text-foreground">
            {appName || t("chat.title", "Assistant")}
          </span>
        </div>
      )}
      <div className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={newThread}
          aria-label={t("chat.new", "New chat")}
          className="rounded p-1 text-muted-foreground transition hover:bg-elevated hover:text-foreground"
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => {
            setHistoryOpen((v) => !v);
            void loadThreads();
          }}
          aria-label={t("chat.history", "Conversations")}
          aria-pressed={historyOpen}
          className={`rounded p-1 transition hover:bg-elevated hover:text-foreground ${
            standalone ? "md:hidden" : ""
          } ${historyOpen ? "text-accent" : "text-muted-foreground"}`}
        >
          <History className="h-4 w-4" />
        </button>
        {!standalone && onExpand && (
          <button
            type="button"
            onClick={onExpand}
            aria-label={t("chat.expand", "Apri a tutta pagina")}
            className="rounded p-1 text-muted-foreground transition hover:bg-elevated hover:text-foreground"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={closePanel}
          aria-label={standalone ? t("chat.back", "Indietro") : t("chat.close", "Close")}
          className="rounded p-1 text-muted-foreground transition hover:bg-elevated hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  // The main chat column (header + transcript/mobile-history + composer). Shared by
  // both surfaces; `historyOpen` swaps the transcript for the list on the widget +
  // the standalone MOBILE overlay (desktop standalone keeps the persistent rail).
  const chatColumnEl = (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {headerEl}
      {historyOpen ? threadListEl : transcriptEl}
      {!historyOpen && composerEl}
    </div>
  );

  return (
    <>
      {/* Launcher — bottom-right (shifted left when the voice orb is also on).
          Suppressed on the standalone surface (it boots open, nothing to launch). */}
      {!standalone && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t("chat.open", "Open chat assistant")}
          className={`fixed bottom-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg transition hover:scale-105 ${
            sideOffset ? "right-24" : "right-5"
          }`}
        >
          <MessageSquare className="h-6 w-6" strokeWidth={1.75} />
        </button>
      )}

      {open &&
        (standalone ? (
          // ChatGPT-style autonomous surface: a persistent conversations rail on the
          // left (desktop) + the main thread column. The host owns the viewport/theme.
          <div className="relative flex h-full w-full overflow-hidden bg-surface">
            <aside className="hidden w-72 shrink-0 flex-col border-r border-border bg-background/40 md:flex">
              <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
                <MessageSquare className="h-4 w-4 text-accent" strokeWidth={2} />
                <span className="truncate text-sm font-semibold text-foreground">
                  {appName || t("chat.title", "Assistant")}
                </span>
              </div>
              {threadListEl}
            </aside>
            {chatColumnEl}
          </div>
        ) : (
          <div
            role="dialog"
            aria-label={t("chat.title", "Assistant")}
            className="fixed bottom-5 right-5 z-[90] flex h-[32rem] max-h-[80vh] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-2xl"
          >
            {chatColumnEl}
          </div>
        ))}
    </>
  );
}
